import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
  } from "react";
  import AsyncSelect from "react-select/async";
  import { useMapsLibrary } from "@vis.gl/react-google-maps";
  
  
  import {
    components,
  } from "react-select";
  
  
  import { twMerge } from "tailwind-merge";
  import classNames from "classnames";

  
import { EditIcon, MapPin, Pin } from "lucide-react";
import { useAutocompleteSuggestions } from "../../hooks/useAutocompleteSuggestions";
import useDebounce from "../../hooks/useDebounce";
  
  const LocationIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        flexShrink: 0,
      }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 3C8.13401 3 5 6.13401 5 10C5 11.8921 5.85317 13.678 7.29228 15.5467C8.50741 17.1245 10.0627 18.6673 11.7323 20.3233C11.8212 20.4115 11.9105 20.5 12 20.5889C12.0895 20.5 12.1788 20.4115 12.2677 20.3233C13.9373 18.6673 15.4926 17.1245 16.7077 15.5467C18.1468 13.678 19 11.8921 19 10C19 6.13401 15.866 3 12 3ZM3 10C3 5.02944 7.02944 1 12 1C16.9706 1 21 5.02944 21 10C21 12.5262 19.8532 14.7402 18.2923 16.767C16.988 18.4607 15.3185 20.1156 13.6508 21.7689C13.3354 22.0816 13.02 22.3943 12.7071 22.7071C12.3166 23.0976 11.6834 23.0976 11.2929 22.7071C10.98 22.3943 10.6646 22.0816 10.3492 21.7689C8.68147 20.1156 7.01205 18.4607 5.70772 16.767C4.14683 14.7402 3 12.5262 3 10ZM12 8C10.8954 8 10 8.89543 10 10C10 11.1046 10.8954 12 12 12C13.1046 12 14 11.1046 14 10C14 8.89543 13.1046 8 12 8ZM8 10C8 7.79086 9.79086 6 12 6C14.2091 6 16 7.79086 16 10C16 12.2091 14.2091 14 12 14C9.79086 14 8 12.2091 8 10Z"
        fill="#4A534C"
      />
    </svg>
  );
  
  const Control = (props, {size}) => {
    const hasValue = props.hasValue || props.selectProps.inputValue;
    const sizeVariant = size || "md";
  
    return (
      <components.Control {...props}>
        <div
          className="flex flex-row gap-1 justify-between w-full items-center px-1 text-ellipsis overflow-hidden whitespace-nowrap"
        >
          <MapPin />
          {props.children}
        </div>
      </components.Control>
    );
  };
  
  const CustomOption = (props) => {  
    return (
      <components.Option {...props}>
        <div className="flex items-center gap-2">
          <LocationIcon />
          {props.children}
        </div>
      </components.Option>
    );
  };
  
  const CustomSingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <div className="flex items-center ">
          {/* <LocationIcon /> */}
          {props.children}
        </div>
      </components.SingleValue>
    );
  };
  
  export function AddressAutocomplete({
    onPlaceSelect,
    address,
    name,
    label,
    editable,
    customPlaceSelect,
    customStyles,
    placeholder,
    required,
    error,
    size = "md",
    showDropdownIndicator,
    typeVariant = "default",
    customClassnames,
    setExternalInputValue,
    customCallback,
    ...props
  }) {
    
    const places = useMapsLibrary("places");
    const debounce = useDebounce();
  

    const containerRef = useRef(null);
    const { fetchSuggestions, resetSession, isGoogleLibLoaded } = useAutocompleteSuggestions({
      includedPrimaryTypes: ["establishment", "geocode"],
      includedRegionCodes: ["us"],
      // "locationBias": {
      //   "circle": {
      //     "center": {
      //       "lat": 39.743190293989066,
      //       "lng":  -104.98801641772356
      //     },
      //     "radius": 5000.0
      //   },
      // }
    });
  
    const stableFetchSuggestions = useCallback(fetchSuggestions, []);

    const onInputChange = useCallback(
      (value, action) => {
        if (action.action === "input-change") {
          setExternalInputValue?.(value);
        }
      },
      [setExternalInputValue]
    )

    const debouncedFetcherRef = useRef(
      debounce(async (inputValue, callback) => {
        const manualOption = {
          value: "manual",
          label: "Enter address manually",
          place_id: "",
          isManual: true,
        };
        
        const suggestions = await stableFetchSuggestions(inputValue);
    
        let opts = suggestions.map((s) => ({
          value: s.placePrediction?.placeId ?? "",
          label: s.placePrediction?.text.text ?? "",
          place_id: s.placePrediction?.placeId ?? "",
          types: s.placePrediction?.types,
          suggestion: s,
        }));
        callback(opts);
      }, 300)
    );
  
    const loadOptions = useMemo(() => {
      return (inputValue) =>
        new Promise((resolve) =>
          debouncedFetcherRef.current(inputValue, resolve)
        );
    }, []);
  
    const extractAddressComponents = useCallback(
      (addressComponents) => {
        const components = {};
        addressComponents.forEach((c) => {
          const types = c.types || [];
          if (types.includes("street_number"))
            components.street_number = c.longText || "";
          if (types.includes("route")) components.route = c.longText || "";
          if (types.includes("locality")) components.city = c.longText || "";
          if (types.includes("administrative_area_level_1")) components.state = c.shortText || "";
          if (types.includes("postal_code"))
            components.postal_code = c.longText || "";
          if (types.includes("country")) components.country = c.longText || "";
        });
        const street = [components.street_number, components.route]
          .filter(Boolean)
          .join(" ");
        return {
          street_number: components.street_number,
          street_name: components.route,
          street_address: street,
          city: components.city || "",
          state: components.state || "",
          postal_code: components.postal_code || "",
          country: components.country || "",
        };
      },
      []
    );
  
    const handleSelect = useCallback(
      async (option) => {
        if (!places || !option) {
          onPlaceSelect?.(null);
          return;
        }
  
        const suggestion = option.suggestion;
        if (!suggestion?.placePrediction) return;
  
        const place = suggestion.placePrediction.toPlace();
  
        const fullPlaceDetails = await place.fetchFields({
          fields: [
            "viewport",
            "location",
            "addressComponents",
            "formattedAddress",
            "svgIconMaskURI",
            "iconBackgroundColor",
          ],
        });
  
        // Reset session (important for Google usage!)
        resetSession();
      
        const addressComponents = extractAddressComponents(
          fullPlaceDetails.place.addressComponents
        );

        const formattedAddress = `${fullPlaceDetails.place.formattedAddress}, USA` || "";
        const coordinates = {
          lat: fullPlaceDetails.place.location?.lat() || 0,
          lng: fullPlaceDetails.place.location?.lng() || 0,
        };
        const addr = {
          option,
          formattedAddress,
          addressComponents,
          coordinates
        }
        customCallback?.(addr);
  
        // Finally pass place up
        onPlaceSelect?.(fullPlaceDetails.place);
        setExternalInputValue?.(fullPlaceDetails.place.formattedAddress || "");
      },
      [places, onPlaceSelect, resetSession, setExternalInputValue]
    );
  
    const defaultStyles = (
      typeVariant,
      size,
      error,
      showDropdownIndicator
    ) => ({
      control: (provided, state) => ({
        ...provided,
        border: "none",
        outline: error
          ? "#f74237 !important"
          : typeVariant === "none"
          ? "none"
          : typeVariant === "default"
          ? "#14201724 !important"
          : provided.border,
        outlineWidth: "2px !important",
        outlineStyle: "solid !important",
        borderRadius: "3px",
        borderColor: undefined,
        boxShadow: undefined,
        minHeight: 20,
        paddingLeft: 4,
        "&:focus-within": error
          ? {
              outline: "#f74237 !important",
              outlineWidth: "2px !important",
              outlineStyle: "solid !important",
            }
          : typeVariant !== "none"
          ? {
              outline: error ? "#f74237 !important" : "#51ae53 !important",
              outlineWidth: "2px !important",
              outlineStyle: "solid !important",
            }
          : {},
        "&:hover":
          typeVariant !== "none"
            ? {
                backgroundColor: "#f6f6f6",
              }
            : {},
      }),
      valueContainer: (provided) => ({
        ...provided,
  
        paddingLeft: size === "sm" ? 4 : size === "lg" ? 14 : 8,
        paddingRight: size === "sm" ? 4 : size === "lg" ? 14 : 8,
        paddingTop: size === "sm" ? 4 : size === "lg" ? 14 : 8,
        paddingBottom: size === "sm" ? 4 : size === "lg" ? 14 : 8,
        borderRadius: 3,
        // "&:focus-within": error
        //   ? {
        //       outline: "#f74237",
        //       outlineWidth: "2px",
        //       outlineStyle: "solid",
        //     }
        //   : typeVariant !== "none"
        //   ? {
        //       outline: error ? "#f74237" : "#51ae53",
        //       outlineWidth: "2px",
        //       outlineStyle: "solid",
        //     }
        //   : {},
        // "&:hover":
        //   typeVariant !== "none"
        //     ? {
        //         backgroundColor: "#f6f6f6",
        //       }
        //     : {},
  
        margin: 0,
      }),
      inputContainer: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
        display: "flex",
        alignItems: "center",
      }),
      input: (provided) => ({
        ...provided,
      }),
      placeholder: (provided) => ({
        ...provided,
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        display: showDropdownIndicator ? "block" : "none",
        padding: size === "sm" ? 4 : 8,
      }),
      clearIndicator: (provided) => ({
        ...provided,
  
        padding: 0,
      }),
      multiValue: (provided, state) => {
        return {
          ...provided,
          borderRadius: 3,
          backgroundColor: "transparent",
          border: "1px solid #d1d5db",
          padding: 0,
          "&:hover .flex-select__multi-value__remove": {
            visibility: state.selectProps.isSelectFocused ? "visible" : "hidden",
            pointerEvents: state.selectProps.isSelectFocused ? "auto" : "none",
          },
        };
      },
      multiValueLabel: (provided) => ({
        ...provided,
        fontSize: size === "sm" ? 14 : size === "lg" ? 16 : 14,
      }),
      multiValueRemove: (provided, state) => ({
        ...provided,
        visibility: state.selectProps.isSelectFocused ? "visible" : "hidden",
        pointerEvents: state.selectProps.isSelectFocused ? "auto" : "none",
        "&:hover": {
          backgroundColor: "transparent",
          color: "black",
        },
      }),

      // ----------- TO TEST ------- TO ADD: portal
      // Dropdown menu container
      menu: (provided) => ({
        ...provided,
        zIndex: 9999,
        position: 'absolute',
      }),
      
      menuList: (provided) => ({
        ...provided,
        WebkitOverflowScrolling: 'touch',
        overflowY: 'auto',
        maxHeight: '180px',
      }),
      
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
      }),
      // ----------- TO TEST -------
      option: (provided, state) => ({
        ...provided,
      }),
      singleValue: (provided) => ({
        ...provided,
        borderRadius: 3,
        fontSize: size === "sm" ? 14 : size === "lg" ? 16 : 14,
      }),
  
      // No options message
      noOptionsMessage: (provided) => ({
        ...provided,
      }),
  
      // Loading message
      loadingMessage: (provided) => ({
        ...provided,
      }),
  
      // The entire container wrapping the input (outermost div)
      container: (provided) => ({
        ...provided,
      }),
  
      indicatorSeparator: (provided) => ({
        ...provided,
        display: "none",
      }),
    });

    const mergedStyles = useMemo(
      () => ({
        ...defaultStyles(typeVariant, size, error, showDropdownIndicator),
        ...(customStyles || {}),
      }),
      [typeVariant, size, error, customStyles, showDropdownIndicator]
    );
    const handleFocus = () => {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300); // wait for keyboard to open
    };
    return (
      <div
        ref={containerRef}
        className={twMerge(
          classNames("relative flex flex-col gap-3", customClassnames?.container)
        )}
      >
        {label && (
          <p className="default-text-subtle block text-sm font-medium text-gray-700 mb-2">
            {label}
            {required && <span className="text-danger-bg-bold">*</span>}
          </p>
        )}
        <AsyncSelect
          onFocus={handleFocus}
          cacheOptions
          name={name}
          isSearchable={editable !== false}
          openMenuOnClick={editable !== false}
          defaultOptions
          value={address?.formattedAddress
            ? { value: address.formattedAddress, label: address.formattedAddress } : null}
          loadOptions={loadOptions}
          onInputChange={onInputChange}
          onChange={handleSelect}
          placeholder={placeholder || "Search for an address"}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue,
            Control: (props) => Control(props, { size }),
            LoadingIndicator: () => null,
          }}
          isClearable={false}
          styles={{
            ...mergedStyles,
            // menuPortal: (base) => ({
            //   ...base,
            //   zIndex: 9999,
            //   // Fix mobile keyboard viewport shift
            //   position: 'fixed',
            // }),
          }}
          className="w-full"
          menuPlacement="auto" // Ensure menu appears below input
          menuPosition="fixed"
          menuPortalTarget={document.body}
          // menuPortalTarget={document.getElementById('address-container')}
          menuShouldScrollIntoView={false}
        />
        <div className="absolute right-0 bottom-[-19px]">
          {error && (
            <div className="  text-danger-bg-bold font-semibold text-xs  rounded">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }
  